import { Formik, FormikValues } from "formik";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { Dispatch, SetStateAction } from "react";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackUserEvent } from "@/analytics/tracking";

import { useCurrentLocale } from "@/context/LanguageContext";

import { Consumer } from "../../../../interfaces/gift-cards/gift-card-interface";
import Footer from "../footer";
import FormOverlay from "../form-overlay";
import BuyerDetailsForm from "./buyer-details-form";

type FormErrors = {
    [field: string]: string | string[];
};

type Props = {
    setFadeOut: () => void;
    active?: boolean;
    consumer: Consumer;
    setConsumer: Dispatch<SetStateAction<FormikValues>>;
    confirmOrder: Dispatch<SetStateAction<boolean>>;
};

const emailRegex = new RegExp(".+\\@.+\\..+");

const BuyForYourselfModal: React.FC<Props> = ({
    setFadeOut,
    active,
    consumer,
    setConsumer,
    confirmOrder,
}) => {
    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const router = useRouter();

    const eventProps = {
        newFlow: false,
    };
    return (
        <Formik
            initialValues={consumer}
            validate={(values) => {
                const errors: FormErrors = {};

                if (!values.email) {
                    errors.email = t("giftCards.forms.errorMessages.required");
                } else if (!emailRegex.test(values.email)) {
                    errors.email = t("giftCards.forms.errorMessages.invalidEmail");
                } else if (values.email !== values.confirmEmail) {
                    errors.email = t("giftCards.forms.errorMessages.unconfirmedEmail");
                }

                if (!values.confirmEmail) {
                    errors.confirmEmail = t("giftCards.forms.errorMessages.required");
                } else if (!emailRegex.test(values.confirmEmail)) {
                    errors.confirmEmail = t("giftCards.forms.errorMessages.invalidEmail");
                } else if (values.confirmEmail !== values.email) {
                    errors.confirmEmail = t("giftCards.forms.errorMessages.unconfirmedEmail");
                }

                if (!values.firstname) {
                    errors.firstname = t("giftCards.forms.errorMessages.required");
                }

                if (!values.lastname) {
                    errors.lastname = t("giftCards.forms.errorMessages.required");
                }

                return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
                trackUserEvent(
                    locale,
                    router.asPath,
                    UI_CONTEXTS.GIFTCARD_BUYFORYOURSELF_FORM,
                    EVENTS.GIFT_CARD_BUY_FOR_YOURSELF_CONFIRM_CLICK,
                    eventProps
                );
                setTimeout(() => {
                    setSubmitting(false);
                    confirmOrder(true);
                    setConsumer(values);
                    setFadeOut();
                }, 400);
            }}
        >
            {(formikProps) => (
                <FormOverlay
                    headerText={t("giftCards.forms.buyForYourself.header")}
                    body={<BuyerDetailsForm formikProps={formikProps} />}
                    footer={<Footer {...formikProps} />}
                    active={active}
                    setFadeOut={setFadeOut}
                />
            )}
        </Formik>
    );
};

export default BuyForYourselfModal;
